/**
 * Wingman SurrealDB Client
 * Manages user profiles, session state, and tier data.
 * Connects via WebSocket to a SurrealDB instance.
 */

const SURREAL_ENDPOINT = import.meta.env.VITE_SURREAL_ENDPOINT;
if (!SURREAL_ENDPOINT && import.meta.env.PROD) {
    console.warn('[SurrealDB] No production endpoint configured. Data persistency LIMITED.');
}
const SURREAL_NS = import.meta.env.VITE_SURREAL_NAMESPACE ?? 'wingman';
const SURREAL_DB = import.meta.env.VITE_SURREAL_DATABASE ?? 'miami';

class SurrealClientImpl {
    constructor() {
        this.ws = null;
        this.connected = false;
        this.msgId = 0;
        this.pending = new Map();
    }

    async connect() {
        if (this.connected) return;

        return new Promise((resolve, reject) => {
            if (typeof window === 'undefined') {
                reject(new Error('SurrealDB requires browser environment'));
                return;
            }

            const timeoutId = setTimeout(() => {
                if (!this.connected) {
                    this.ws.close();
                    reject(new Error('SurrealDB connection timed out'));
                }
            }, 3000);

            this.ws = new WebSocket(SURREAL_ENDPOINT);

            this.ws.onopen = async () => {
                clearTimeout(timeoutId);
                await this._send('use', [SURREAL_NS, SURREAL_DB]);
                this.connected = true;
                console.info('[SurrealDB] Connected to Wingman data layer');
                resolve();
            };

            this.ws.onmessage = (event) => {
                const msg = JSON.parse(event.data);
                const handler = this.pending.get(msg.id);
                if (handler) {
                    if (msg.error) handler.reject(msg.error);
                    else handler.resolve(msg.result);
                    this.pending.delete(msg.id);
                }
            };

            this.ws.onerror = (err) => {
                console.error('[SurrealDB] Connection error', err);
                reject(err);
            };
        });
    }

    _send(method, params) {
        return new Promise((resolve, reject) => {
            if (!this.ws) { reject(new Error('Not connected')); return; }
            const id = ++this.msgId;
            this.pending.set(id, { resolve, reject });
            this.ws.send(JSON.stringify({ id, method, params }));
        });
    }

    async getOrCreateProfile(uid, email) {
        try {
            await this.connect();
        } catch {
            // Offline fallback — return default free profile
            return { id: uid, uid, email, tier: 'free', createdAt: new Date().toISOString(), lastLogin: new Date().toISOString() };
        }

        // 🛡️ SEC-02 — Parameterized SurrealQL. User-controlled values are passed as
        // bound variables (never interpolated into the query string), so input can
        // only ever be DATA, not QUERY STRUCTURE.
        const result = await this._send('query', ['SELECT * FROM user WHERE uid = $uid', { uid }]);
        const existing = result?.[0]?.result?.[0];

        if (existing) {
            // Update last login
            await this._send('query', ['UPDATE user SET lastLogin = time::now() WHERE uid = $uid', { uid }]);
            return existing;
        }

        // Create new profile with Free tier
        const created = await this._send('query', [
            'CREATE user CONTENT { uid: $uid, email: $email, tier: "free", createdAt: time::now(), lastLogin: time::now() }',
            { uid, email }
        ]);

        return created?.[0]?.result?.[0];
    }

    async updateTier(uid, tier) {
        await this.connect();
        // 🛡️ SEC-02 — parameterized (bound variables only).
        await this._send('query', ['UPDATE user SET tier = $tier WHERE uid = $uid', { tier, uid }]);
        console.info(`[SurrealDB] Tier updated to ${tier} for UID: ${uid}`);
    }

    isConnected() { return this.connected; }
}

// Exported for security unit tests (SEC-02). Runtime uses the singleton below.
export { SurrealClientImpl };
export const surrealClient = new SurrealClientImpl();
